// Pure personal/professional context model. This module intentionally has no
// React or Firebase dependencies so storage, search, and UI code can share the
// same compatibility rules while the legacy connection shape is migrated.
import {
  PUBLISHED_PROFILE_SEARCH_FIELDS,
} from "./profilePublication";
import {
  contactProfileForEntry,
  normalizePrivateContactProfile,
} from "./privateContactProfile";
import {
  closenessFor,
  closenessFromStrength,
  strengthFromCloseness,
} from "./connectionDetails";

export const CONTEXT_PROFESSIONAL = "professional";
export const CONTEXT_PERSONAL = "personal";
export const CONTEXT_ALL = "all";

export const CONNECTION_CONTEXTS = Object.freeze({
  PROFESSIONAL: CONTEXT_PROFESSIONAL,
  PERSONAL: CONTEXT_PERSONAL,
  ALL: CONTEXT_ALL,
});

export const DEFAULT_CONNECTION_CONTEXT = CONTEXT_PROFESSIONAL;

export const FACET_CONTEXTS = Object.freeze([
  CONTEXT_PROFESSIONAL,
  CONTEXT_PERSONAL,
]);

export function normalizeConnectionContext(
  context,
  fallback = DEFAULT_CONNECTION_CONTEXT
) {
  return context === CONTEXT_PROFESSIONAL ||
    context === CONTEXT_PERSONAL ||
    context === CONTEXT_ALL
    ? context
    : fallback;
}

function firstDefined(...values) {
  return values.find((value) => value !== undefined && value !== null);
}

function firstText(...values) {
  const value = values.find(
    (candidate) => typeof candidate === "string" && candidate.trim()
  );
  return value ? value.trim() : "";
}

/** Canonical shared identity for registered and private/name-only people. */
export function identityOf(entry) {
  const identity = entry?.identity || {};
  const profile = entry?.otherProfile || identity.profile || {};
  return {
    ...identity,
    user_uid:
      firstDefined(
        identity.user_uid,
        identity.userId,
        identity.registered_user_id,
        identity.uid,
        entry?.person_uid,
        entry?.recipient_uid,
        profile.id
      ) || null,
    full_name: firstText(
      identity.full_name,
      identity.name,
      profile.full_name,
      entry?.recipient_name
    ),
    phone: firstText(identity.phone, entry?.recipient_phone, profile.phone),
  };
}

/** Facet contexts projected by a Personal, Professional, or All view. */
export function facetContextsFor(context = DEFAULT_CONNECTION_CONTEXT) {
  const selected = normalizeConnectionContext(context);
  return selected === CONTEXT_ALL ? [...FACET_CONTEXTS] : [selected];
}

export function defaultDiscoveryForContext(
  context = DEFAULT_CONNECTION_CONTEXT
) {
  return normalizeConnectionContext(context) === CONTEXT_PROFESSIONAL;
}

/** Canonical defaults for a newly-created stored facet. */
export function facetDefaultsForContext(
  context = DEFAULT_CONNECTION_CONTEXT
) {
  const selected = FACET_CONTEXTS.includes(context)
    ? context
    : DEFAULT_CONNECTION_CONTEXT;
  return {
    context: selected,
    relationship_type:
      selected === CONTEXT_PROFESSIONAL ? "colleague" : "friend",
    connection_strength: "moderate",
    closeness: closenessFromStrength("moderate"),
    where_met: "",
    private_note: "",
    last_interacted_at: null,
    discovery_enabled: defaultDiscoveryForContext(selected),
    origin: null,
  };
}

/**
 * Canonical map origin stored independently inside each facet. `parent_uid`
 * points to a registered user; `parent_entry_id` supports a private/name-only
 * rolodex entry. Legacy branch data remains available through `parent_key`.
 */
export function normalizeOrigin(origin, legacy = null) {
  const source = origin || {};
  const legacyParent = legacy?.branch_parent_uid || null;
  const parentUid =
    firstDefined(
      source.parent_uid,
      source.user_uid,
      source.parentUid,
      legacy?.origin_user_uid
    ) || null;
  const parentEntryId =
    firstDefined(
      source.parent_entry_id,
      source.entry_id,
      source.parentEntryId,
      legacy?.origin_entry_id,
      legacy?.branch_parent_entry_id
    ) || null;
  const parentKey =
    firstDefined(source.parent_key, source.key, legacyParent, parentUid, parentEntryId) ||
    null;
  if (!parentKey) return null;

  const kind =
    source.kind || (parentEntryId ? "entry" : parentUid || legacyParent ? "user" : null);
  const parentName = firstText(
    source.parent_name,
    source.name,
    source.parentName,
    legacy?.origin_name,
    legacy?.branch_parent_name
  );

  return {
    // Discriminated fields used by the context graph and persisted facets.
    kind,
    user_uid: kind === "user" ? parentUid || legacyParent || null : null,
    entry_id: kind === "entry" ? parentEntryId : null,
    name: parentName,
    mode:
      firstDefined(
        source.mode,
        source.branch_mode,
        legacy?.origin_mode,
        legacy?.branch_mode
      ) || null,
    // Explicit aliases make the target type obvious to storage/migration code;
    // parent_key retains the ambiguous legacy branch id without guessing.
    parent_uid: parentUid,
    parent_entry_id: parentEntryId,
    parent_key: parentKey,
    parent_name: parentName,
  };
}

export function normalizeRolodexFacet(facet, context, legacy = null) {
  const source = facet || {};
  const defaults = facetDefaultsForContext(context);

  // Closeness (1-5) is what the profile page edits; connection_strength
  // (weak/moderate/strong) stays the stored truth every other screen reads.
  // Whichever the caller supplied is authoritative, and the other is derived.
  const storedStrength =
    firstDefined(
      source.connection_strength,
      source.connectionStrength,
      legacy?.connection_strength,
      defaults.connection_strength
    ) || defaults.connection_strength;
  const suppliedCloseness = firstDefined(source.closeness, legacy?.closeness);
  const closeness =
    suppliedCloseness === undefined || suppliedCloseness === null
      ? closenessFromStrength(storedStrength)
      : closenessFor({ closeness: suppliedCloseness, connection_strength: storedStrength });
  const connectionStrength =
    suppliedCloseness === undefined || suppliedCloseness === null
      ? storedStrength
      : strengthFromCloseness(closeness);

  return {
    ...source,
    context: defaults.context,
    closeness,
    relationship_type:
      firstDefined(
        source.relationship_type,
        source.relationshipType,
        legacy?.relationship_type,
        defaults.relationship_type
      ) || defaults.relationship_type,
    connection_strength: connectionStrength,
    where_met:
      firstDefined(
        source.where_met,
        source.whereMet,
        legacy?.where_met,
        defaults.where_met
      ) || defaults.where_met,
    private_note:
      firstDefined(
        source.private_note,
        source.privateNote,
        legacy?.private_note,
        defaults.private_note
      ) || defaults.private_note,
    last_interacted_at:
      firstDefined(
        source.last_interacted_at,
        source.lastInteractedAt,
        legacy?.last_interacted_at,
        defaults.last_interacted_at
      ) || defaults.last_interacted_at,
    discovery_enabled:
      firstDefined(
        source.discovery_enabled,
        source.discoveryEnabled,
        legacy?.discovery_enabled,
        defaults.discovery_enabled
      ) === true,
    origin: normalizeOrigin(source.origin, legacy || source),
  };
}

/** A stable person key: registered uid, otherwise the private entry id. */
export function personKeyOf(entry) {
  if (!entry) return null;
  const identity = identityOf(entry);
  return identity.user_uid || entry.personKey || entry.id || null;
}

/** Resolve an origin to both its private entry and rendered person key. */
export function resolveOriginTarget(origin, entries = []) {
  const normalized = normalizeOrigin(origin);
  if (!normalized) {
    return { origin: null, entry: null, entryId: null, personKey: null };
  }

  let entry = null;
  if (normalized.kind === "entry" && normalized.entry_id) {
    entry = (entries || []).find((item) => item?.id === normalized.entry_id) || null;
  } else if (normalized.user_uid) {
    entry =
      (entries || []).find(
        (item) => identityOf(item).user_uid === normalized.user_uid
      ) || null;
  }

  // An unresolved legacy branch key can name either a private entry or a user.
  if (!entry && normalized.parent_key) {
    entry =
      (entries || []).find(
        (item) =>
          item?.id === normalized.parent_key ||
          personKeyOf(item) === normalized.parent_key
      ) || null;
  }

  return {
    origin: normalized,
    entry,
    entryId: entry?.id || normalized.entry_id || null,
    personKey:
      personKeyOf(entry) ||
      normalized.user_uid ||
      normalized.entry_id ||
      normalized.parent_key ||
      null,
  };
}

/** Build a stored origin from the person key selected by a branch picker. */
export function originForTarget(
  parentKey,
  entries = [],
  { name = "", mode = "direct" } = {}
) {
  if (!parentKey) return null;
  const parentEntry =
    (entries || []).find(
      (entry) => entry?.id === parentKey || personKeyOf(entry) === parentKey
    ) || null;

  if (parentEntry) {
    const origin = normalizeOrigin({
      kind: "entry",
      entry_id: parentEntry.id,
      name:
        name ||
        identityOf(parentEntry).full_name ||
        parentEntry.otherProfile?.full_name ||
        parentEntry.recipient_name ||
        "",
      mode,
    });
    return {
      kind: origin.kind,
      entry_id: origin.entry_id,
      name: origin.name,
      mode: origin.mode || "direct",
    };
  }

  const origin = normalizeOrigin({
    kind: "user",
    user_uid: parentKey,
    name,
    mode,
  });
  return {
    kind: origin.kind,
    user_uid: origin.user_uid,
    name: origin.name,
    mode: origin.mode || "direct",
  };
}

/**
 * Convert one legacy `connections` document into a professional rolodex entry.
 * The original fields (including id and otherProfile) are retained so callers
 * can adopt facets incrementally without breaking the current UI.
 */
export function normalizeLegacyConnection(connection) {
  if (!connection) return null;
  const identity = identityOf(connection);
  return {
    ...connection,
    identity,
    personKey: identity.user_uid || connection.id || null,
    private_profile: normalizePrivateContactProfile(connection.private_profile),
    facets: {
      [CONTEXT_PROFESSIONAL]: normalizeRolodexFacet(
        { discovery_enabled: connection.discovery_enabled ?? true },
        CONTEXT_PROFESSIONAL,
        connection
      ),
    },
  };
}

/** Normalize either the new rolodex shape or a legacy connection document. */
export function normalizeRolodexEntry(entry) {
  if (!entry) return null;
  if (!entry.facets) return normalizeLegacyConnection(entry);

  const identity = identityOf(entry);
  const facets = {};
  for (const context of FACET_CONTEXTS) {
    if (entry.facets[context]) {
      facets[context] = normalizeRolodexFacet(entry.facets[context], context);
    }
  }

  return {
    ...entry,
    identity,
    personKey: identity.user_uid || entry.personKey || entry.id || null,
    private_profile: normalizePrivateContactProfile(entry.private_profile),
    facets,
  };
}

export function normalizeLegacyConnections(connections) {
  return (connections || []).map(normalizeLegacyConnection).filter(Boolean);
}

export function normalizeRolodexEntries(entries) {
  return (entries || []).map(normalizeRolodexEntry).filter(Boolean);
}

/** Return the personal/professional contexts actually present on an entry. */
export function facetsFor(entry) {
  const normalized = normalizeRolodexEntry(entry);
  if (!normalized) return [];
  return FACET_CONTEXTS.filter((context) => Boolean(normalized.facets[context]));
}

export function hasFacet(entry, context) {
  if (!FACET_CONTEXTS.includes(context)) return false;
  return facetsFor(entry).includes(context);
}

/**
 * Pick the facet shown by a context-specific surface. In All, the preferred
 * tab wins when present, then Professional, then Personal.
 */
export function selectActiveFacet(
  entry,
  context = DEFAULT_CONNECTION_CONTEXT,
  preferredContext = DEFAULT_CONNECTION_CONTEXT
) {
  const normalized = normalizeRolodexEntry(entry);
  if (!normalized) return null;
  const selectedContext = normalizeConnectionContext(context);

  if (selectedContext !== CONTEXT_ALL) {
    return normalized.facets[selectedContext] || null;
  }

  const preferred = FACET_CONTEXTS.includes(preferredContext)
    ? preferredContext
    : DEFAULT_CONNECTION_CONTEXT;
  const fallback =
    preferred === CONTEXT_PROFESSIONAL ? CONTEXT_PERSONAL : CONTEXT_PROFESSIONAL;
  return normalized.facets[preferred] || normalized.facets[fallback] || null;
}

/** Read the origin belonging to the selected facet, never the other context. */
export function originForContext(
  entry,
  context = DEFAULT_CONNECTION_CONTEXT,
  preferredContext = DEFAULT_CONNECTION_CONTEXT
) {
  return selectActiveFacet(entry, context, preferredContext)?.origin || null;
}

function projectedFacetContext(entry, context) {
  const contexts = facetsFor(entry);
  const requested = normalizeConnectionContext(context, CONTEXT_ALL);
  if (requested !== CONTEXT_ALL && contexts.includes(requested)) return requested;
  return contexts.includes(CONTEXT_PROFESSIONAL)
    ? CONTEXT_PROFESSIONAL
    : contexts[0] || null;
}

/**
 * Flatten one canonical entry into the legacy display shape still consumed by
 * cards, details, and the connection tree.
 */
export function projectEntryToContext(
  entry,
  context = CONTEXT_ALL,
  allEntries = []
) {
  const normalized = normalizeRolodexEntry(entry);
  if (!normalized) return null;
  const selected = projectedFacetContext(normalized, context);
  if (!selected) return null;
  const facet = selectActiveFacet(normalized, selected);
  if (!facet) return null;
  const resolvedOrigin = resolveOriginTarget(facet.origin, allEntries);

  return {
    ...entry,
    relationship_type: facet.relationship_type,
    connection_strength: facet.connection_strength,
    where_met: facet.where_met,
    private_note: facet.private_note,
    last_interacted_at: facet.last_interacted_at,
    branch_parent_uid: resolvedOrigin.personKey,
    branch_parent_name: facet.origin?.name || facet.origin?.parent_name || "",
    branch_mode: facet.origin?.mode || null,
    contexts: facetsFor(normalized),
    activeContext: selected,
  };
}

export function projectEntriesToContext(entries, context = CONTEXT_ALL) {
  const visible = filterEntriesByContext(entries, context);
  return visible
    .map((entry) => projectEntryToContext(entry, context, entries))
    .filter(Boolean);
}

function mergeIdentity(primary, secondary) {
  return {
    ...secondary,
    ...primary,
    user_uid: primary.user_uid || secondary.user_uid || null,
    full_name: primary.full_name || secondary.full_name || "",
    phone: primary.phone || secondary.phone || "",
  };
}

/**
 * Collapse duplicate documents for registered people and combine their facets.
 * Name-only contacts deliberately remain distinct because their entry id is
 * their identity; equal names are not enough evidence that they are one person.
 */
export function dedupeEntriesByPerson(entries) {
  const deduped = [];
  const indexByKey = new Map();

  for (const entry of normalizeRolodexEntries(entries)) {
    const key = personKeyOf(entry);
    const dedupeKey = entry.identity.user_uid
      ? `user:${entry.identity.user_uid}`
      : entry.id
        ? `entry:${entry.id}`
        : null;
    // Entries with no usable id cannot be safely merged.
    if (!dedupeKey || !indexByKey.has(dedupeKey)) {
      if (dedupeKey) indexByKey.set(dedupeKey, deduped.length);
      deduped.push(entry);
      continue;
    }

    const index = indexByKey.get(dedupeKey);
    const existing = deduped[index];
    deduped[index] = {
      ...entry,
      ...existing,
      identity: mergeIdentity(existing.identity, entry.identity),
      personKey: key,
      facets: {
        ...entry.facets,
        ...existing.facets,
      },
    };
  }

  return deduped;
}

/**
 * Return entries visible in one context. All combines dual-context documents
 * for a registered person into one result.
 */
export function filterEntriesByContext(
  entries,
  context = DEFAULT_CONNECTION_CONTEXT
) {
  const selectedContext = normalizeConnectionContext(context);
  const normalized = normalizeRolodexEntries(entries);

  if (selectedContext === CONTEXT_ALL) {
    return dedupeEntriesByPerson(normalized).filter(
      (entry) => facetsFor(entry).length > 0
    );
  }

  return normalized.filter((entry) => hasFacet(entry, selectedContext));
}

function searchableValues(entry, context) {
  const normalized = normalizeRolodexEntry(entry);
  if (!normalized) return [];

  const profile = contactProfileForEntry(normalized);
  const shared = [
    normalized.identity.full_name,
    normalized.identity.phone,
    ...[...PUBLISHED_PROFILE_SEARCH_FIELDS, "email"].map((field) => profile[field]),
  ];
  const selectedContexts =
    context === CONTEXT_ALL ? facetsFor(normalized) : [context];

  for (const facetContext of selectedContexts) {
    const facet = normalized.facets[facetContext];
    if (!facet) continue;
    shared.push(
      facet.relationship_type,
      facet.connection_strength,
      facet.where_met,
      facet.private_note,
      facet.origin?.parent_name
    );
  }
  return shared;
}

/** Match shared identity/profile fields plus only the active facet metadata. */
export function matchesRolodexQuery(
  entry,
  query,
  context = DEFAULT_CONNECTION_CONTEXT
) {
  const selectedContext = normalizeConnectionContext(context);
  if (!query?.trim()) return false;
  if (
    selectedContext !== CONTEXT_ALL &&
    !hasFacet(entry, selectedContext)
  ) {
    return false;
  }

  const needle = query.trim().toLocaleLowerCase();
  return searchableValues(entry, selectedContext).some((value) =>
    String(value ?? "").toLocaleLowerCase().includes(needle)
  );
}

/**
 * Search the owner's rolodex. A blank query returns the current context list;
 * All is deduped before matching so a dual-context person appears once.
 */
export function searchRolodex(
  entries,
  query,
  context = DEFAULT_CONNECTION_CONTEXT
) {
  const selectedContext = normalizeConnectionContext(context);
  const visible = filterEntriesByContext(entries, selectedContext);
  if (!query?.trim()) return visible;
  return visible.filter((entry) =>
    matchesRolodexQuery(entry, query, selectedContext)
  );
}

/** Professional defaults on; Personal requires an explicit opt-in. */
export function isDiscoveryEnabled(facet, context) {
  if (!FACET_CONTEXTS.includes(context) || !facet) return false;
  if (facet.discovery_enabled !== undefined) {
    return facet.discovery_enabled === true;
  }
  if (facet.discoveryEnabled !== undefined) {
    return facet.discoveryEnabled === true;
  }
  return defaultDiscoveryForContext(context);
}

/**
 * A public network edge requires a registered target and at least one enabled
 * facet in the requested context. Local/name-only contacts never publish.
 */
export function discoveryEdgeEligible(entry, context = CONTEXT_ALL) {
  const normalized = normalizeRolodexEntry(entry);
  if (!normalized?.identity.user_uid) return false;

  const selectedContext = normalizeConnectionContext(context, CONTEXT_ALL);
  const contexts = facetContextsFor(selectedContext);
  return contexts.some((facetContext) =>
    isDiscoveryEnabled(normalized.facets[facetContext], facetContext)
  );
}

export const isDiscoveryEdgeEligible = discoveryEdgeEligible;
